import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Loader2, RefreshCw, RotateCw } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { formatDateTimeHuman, cn } from '@/lib/utils'
import type { UseReportRange } from '@/hooks/useReportRange'
import type { FiscalJob } from '@/types'
import { FiscalAuditLog } from '@/components/admin/FiscalAuditLog'
import { PraReconciliationCard } from '@/components/admin/reports/PraReconciliationCard'
import { getStoredUserRole } from '@/lib/staff-roles'

interface Props {
  // Accepted for parity with the other report tabs. The queue and audit log are
  // state/event logs, not date-bucketed, so the global range is intentionally
  // not wired into the queries here.
  range: UseReportRange
}

type ViewFilter = 'attention' | 'dead' | 'all'

const VIEW_OPTIONS = [
  { id: 'attention', label: 'Needs attention' },
  { id: 'dead', label: 'Dead only' },
  { id: 'all', label: 'All jobs' },
] as const

const ATTENTION_STATUSES: FiscalJob['status'][] = ['dead', 'pending', 'processing']

/** Which authority's workflows to show. Defaults to whatever the tenant has
 *  configured, so a PRA tenant is not made to scroll past FBR workflows it will
 *  never use — and a PRA/FBR mix stays inspectable via "All". */
type AuthorityFilter = 'all' | 'pra' | 'fbr'

const AUTHORITY_OPTIONS: Array<{ id: AuthorityFilter; label: string }> = [
  { id: 'all', label: 'All authorities' },
  { id: 'pra', label: 'PRA e-IMS only' },
  { id: 'fbr', label: 'FBR / PRAL only' },
]

function statusBadgeClass(status: FiscalJob['status']): string {
  switch (status) {
    case 'succeeded':
      return 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-200 dark:border-emerald-900/40'
    case 'dead':
      return 'bg-red-50 text-red-800 border-red-200 dark:bg-red-950 dark:text-red-200 dark:border-red-900/40'
    case 'processing':
      return 'bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-950 dark:text-blue-200 dark:border-blue-900/40'
    case 'cancelled':
      return 'bg-muted text-muted-foreground border-border'
    case 'pending':
    default:
      return 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-900/40'
  }
}

/**
 * FiscalQueueTab — the operational view of FBR/PRAL submissions, relocated from
 * Settings → Tax & Fiscal. Two stacked sections:
 *
 *  1. "Needs attention" — the live fiscal_outbound_jobs queue (pending /
 *     processing / dead). Dead jobs (auto-retry exhausted, ~10.5h of backoff)
 *     get a manual **Retry** button + a header **Retry all failed** action.
 *     Retrying re-queues the same job with the same USIN (no payload change);
 *     the worker's F-FBR-03 ledger guard prevents any double submission.
 *
 *  2. The immutable per-attempt audit log (FiscalAuditLog), unchanged.
 */
export function FiscalQueueTab(_props: Props) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [view, setView] = useState<ViewFilter>('attention')
  const [orderQuery, setOrderQuery] = useState('')

  // Read-only roles (accountant) can view the fiscal queue + audit log but
  // not re-queue jobs. The backend never grants them the retry endpoints, so
  // this just hides the dead controls.
  const isReadOnly = getStoredUserRole() === 'accountant'

  const jobsQuery = useQuery({
    queryKey: ['fiscal-jobs'],
    queryFn: () => apiClient.getFiscalJobs(),
    staleTime: 15_000,
  })

  // The tenant's configured authority scopes the queue by default: a PRA tenant
  // should not have to filter past FBR workflows it will never use, and vice
  // versa. Shares the ['fiscal','config'] cache with the settings panel.
  const configQuery = useQuery({
    queryKey: ['fiscal', 'config'],
    queryFn: () => apiClient.getFiscalConfig(),
    staleTime: 60_000,
  })
  const configuredAuthority = String(configQuery.data?.data?.authority ?? '').toUpperCase().trim()
  const defaultAuthorityView: AuthorityFilter = configuredAuthority === 'PRA_EIMS' ? 'pra' : 'all'
  const [authorityView, setAuthorityView] = useState<AuthorityFilter | null>(null)
  const effectiveAuthorityView = authorityView ?? defaultAuthorityView

  const allJobs: FiscalJob[] = useMemo(() => jobsQuery.data?.data ?? [], [jobsQuery.data])

  const deadCount = useMemo(() => allJobs.filter((j) => j.status === 'dead').length, [allJobs])

  const rows = useMemo(() => {
    let data = allJobs
    if (view === 'attention') data = data.filter((j) => ATTENTION_STATUSES.includes(j.status))
    else if (view === 'dead') data = data.filter((j) => j.status === 'dead')
    if (effectiveAuthorityView !== 'all') {
      data = data.filter((j) => {
        const isPra = String(j.workflow ?? '').toUpperCase().includes('PRA_EIMS')
        return effectiveAuthorityView === 'pra' ? isPra : !isPra
      })
    }
    const q = orderQuery.trim().toLowerCase()
    if (q) {
      data = data.filter(
        (j) =>
          (j.order_number ?? '').toLowerCase().includes(q) ||
          (j.order_id ?? '').toLowerCase().includes(q),
      )
    }
    return data
  }, [allJobs, view, effectiveAuthorityView, orderQuery])

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['fiscal-jobs'] })
    queryClient.invalidateQueries({ queryKey: ['fiscal-audit-events'] })
  }

  const retryOne = useMutation({
    mutationFn: (id: string) => apiClient.retryFiscalJob(id),
    onSuccess: () => {
      toast({ title: 'Re-queued', description: 'The fiscal submission will be retried shortly.' })
      invalidate()
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : 'Could not retry this job.'
      toast({ variant: 'destructive', title: 'Retry failed', description: msg })
    },
  })

  const retryAll = useMutation({
    mutationFn: () => apiClient.retryAllFiscalJobs(),
    onSuccess: (res) => {
      const n = res?.data?.retried_count ?? 0
      toast({
        title: n > 0 ? `${n} job${n === 1 ? '' : 's'} re-queued` : 'Nothing to retry',
        description:
          n > 0
            ? 'Failed submissions will be retried shortly.'
            : 'No dead jobs were eligible for retry.',
      })
      invalidate()
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : 'Could not retry failed jobs.'
      toast({ variant: 'destructive', title: 'Retry failed', description: msg })
    },
  })

  const onRetryAll = () => {
    if (deadCount === 0) return
    if (!window.confirm(`Re-queue ${deadCount} failed (dead) fiscal job${deadCount === 1 ? '' : 's'}?`)) return
    retryAll.mutate()
  }

  return (
    <div className="space-y-6">
      {/* Parked PRA submissions come first: an unresolved claim means a sale may
          be unreported to the authority, which outranks a retryable queue job.
          The card renders nothing when there is nothing parked. */}
      <PraReconciliationCard />

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" /> Needs attention
              </CardTitle>
              <CardDescription>
                Live FBR / PRAL submission queue. Failed submissions auto-retry for
                ~10.5 hours, then go <span className="font-medium">dead</span> — retry those here
                once the connection is back (or fix the rejection, then retry).
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => jobsQuery.refetch()}
                disabled={jobsQuery.isFetching}
              >
                <RefreshCw className={cn('w-4 h-4 mr-2', jobsQuery.isFetching && 'animate-spin')} />
                Refresh
              </Button>
              {!isReadOnly && (
                <Button
                  type="button"
                  size="sm"
                  onClick={onRetryAll}
                  disabled={deadCount === 0 || retryAll.isPending}
                >
                  <RotateCw className={cn('w-4 h-4 mr-2', retryAll.isPending && 'animate-spin')} />
                  Retry all failed{deadCount > 0 ? ` (${deadCount})` : ''}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">
                Show
              </label>
              <Select value={view} onValueChange={(v) => setView(v as ViewFilter)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VIEW_OPTIONS.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">
                Authority
              </label>
              <Select
                value={effectiveAuthorityView}
                onValueChange={(v) => setAuthorityView(v as AuthorityFilter)}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AUTHORITY_OPTIONS.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {authorityView === null && defaultAuthorityView !== 'all' && (
                <p className="mt-1 text-[10px] text-muted-foreground">
                  Scoped to this tenant&apos;s configured authority.
                </p>
              )}
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground mb-1 block">
                Order #
              </label>
              <Input
                value={orderQuery}
                onChange={(e) => setOrderQuery(e.target.value)}
                placeholder="Filter by order number"
              />
            </div>
          </div>

          {jobsQuery.isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="ml-2 text-sm text-muted-foreground">Loading fiscal queue…</span>
            </div>
          )}

          {!jobsQuery.isLoading && rows.length === 0 && (
            <div className="text-center py-8 text-sm text-muted-foreground border rounded-md">
              {view === 'attention'
                ? 'No fiscal submissions need attention.'
                : 'No fiscal jobs match the current filters.'}
            </div>
          )}

          {!jobsQuery.isLoading && rows.length > 0 && (
            <div className="overflow-x-auto border rounded-md">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2">When</th>
                    <th className="text-left px-3 py-2">Order</th>
                    <th className="text-left px-3 py-2">Workflow</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-right px-3 py-2">Attempts</th>
                    <th className="text-left px-3 py-2">Last error</th>
                    <th className="text-right px-3 py-2">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((j) => (
                    <tr key={j.id} className="align-top">
                      <td className="px-3 py-2 tabular-nums whitespace-nowrap">
                        {formatDateTimeHuman(j.created_at)}
                      </td>
                      <td className="px-3 py-2 font-mono text-[11px]">
                        {j.order_number ?? j.order_id.slice(0, 8)}
                      </td>
                      <td className="px-3 py-2">{j.workflow}</td>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className={cn('capitalize text-[10px]', statusBadgeClass(j.status))}>
                          {j.status}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {j.attempt_count}/{j.max_attempts}
                      </td>
                      <td
                        className="px-3 py-2 text-red-700 dark:text-red-300 max-w-[280px] truncate"
                        title={j.last_error ?? ''}
                      >
                        {j.last_error ?? ''}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {!isReadOnly && (j.status === 'dead' || j.status === 'cancelled') ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => retryOne.mutate(j.id)}
                            disabled={retryOne.isPending}
                          >
                            <RotateCw className="w-3.5 h-3.5 mr-1.5" />
                            Retry
                          </Button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <FiscalAuditLog />
    </div>
  )
}
